import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { ArrowDown, Calendar } from "lucide-react";

// FIFA World Cup 2026 opening match: Mexico City, June 11, 2026 — 20:00 local (Mexico City = UTC-6)
// → 2026-06-12T02:00:00Z (02:00 UTC)
const KICKOFF_UTC = "2026-06-12T02:00:00Z";

function getCountdown() {
  const now = Date.now();
  const target = new Date(KICKOFF_UTC).getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, isLive: diff === 0 };
}

export default function WMCountdownBanner() {
  const { t, lang } = useI18n();
  const [c, setC] = useState(getCountdown);

  useEffect(() => {
    const id = setInterval(() => setC(getCountdown()), 1000);
    return () => clearInterval(id);
  }, []);

  // Smooth-scroll to product grid AND tell it to activate WM 2026 category
  const handleCTA = useCallback(() => {
    window.dispatchEvent(new CustomEvent("kh:set-category", { detail: "wm2026" }));
    const el = document.getElementById("shop");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Smooth-scroll to newsletter section + focus email input
  const handleBonusCTA = useCallback(() => {
    const el = document.getElementById("newsletter");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        const input = el.querySelector('input[type="email"]');
        if (input) input.focus();
      }, 700);
    }
  }, []);

  const label =
    lang === "en"
      ? {
          eyebrow: "Limited Edition · Drop CH.WM",
          title: "World Cup 2026",
          subtitle: "Official FIFA host nations & teams. Released for the tournament — strictly limited.",
          countdown: "Kickoff in",
          cta: "Shop the WM Collection",
          days: "Days",
          hours: "Hrs",
          minutes: "Min",
          seconds: "Sec",
          live: "It's matchday — the cup is on.",
          calendar: "11 · 06 · 2026 — Mexico City",
          bonus: "Kickoff Bonus",
          bonusText: "15% off your first order — subscribe to the newsletter before kickoff.",
          bonusCta: "Claim 15% →",
        }
      : lang === "es"
      ? {
          eyebrow: "Edición Limitada · Drop CH.MUNDIAL",
          title: "Mundial 2026",
          subtitle: "Selecciones anfitrionas y favoritas FIFA. Lanzada para el torneo — estrictamente limitada.",
          countdown: "Pitido inicial en",
          cta: "Ver Colección Mundial",
          days: "Días",
          hours: "Hrs",
          minutes: "Min",
          seconds: "Seg",
          live: "Día de partido — la copa está en marcha.",
          calendar: "11 · 06 · 2026 — Ciudad de México",
          bonus: "Bono de Apertura",
          bonusText: "15% de descuento en tu primer pedido — suscríbete al newsletter antes del pitido inicial.",
          bonusCta: "Obtener 15% →",
        }
      : {
          eyebrow: "Limited Edition · Drop CH.WM",
          title: "WM 2026 Edition",
          subtitle: "Offizielle Gastgeber & Favoriten der FIFA WM 2026. Streng limitiert für das Turnier.",
          countdown: "Anpfiff in",
          cta: "Zur WM-Kollektion",
          days: "Tage",
          hours: "Std",
          minutes: "Min",
          seconds: "Sek",
          live: "Anpfiff! Der Pokal rollt.",
          calendar: "11 · 06 · 2026 — Mexico City",
          bonus: "Anpfiff-Bonus",
          bonusText: "15 % auf deine erste Bestellung — Newsletter sichern, bevor der Pokal rollt.",
          bonusCta: "15 % sichern →",
        };

  return (
    <section
      data-testid="wm-countdown-banner"
      className="relative overflow-hidden py-20 sm:py-28 border-y border-[#1f1f29]"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(123, 30, 46, 0.45), rgba(5, 5, 5, 1) 70%)",
      }}
    >
      {/* Field-stripe pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, transparent 0 80px, #ffffff 80px 81px)",
        }}
      />

      {/* Gold edge top & bottom */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: Title block */}
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
              {label.eyebrow}
            </span>
          </div>

          <h2
            data-testid="wm-banner-title"
            className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.88] text-white"
          >
            {label.title.split(" ")[0]}{" "}
            <span className="font-serif-luxury italic text-[#D4AF37] normal-case tracking-normal">
              {label.title.split(" ").slice(1).join(" ").toLowerCase()}
            </span>
          </h2>

          <p className="mt-6 font-body text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
            {label.subtitle}
          </p>

          <div className="mt-6 flex items-center gap-2 text-white/55">
            <Calendar className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="font-body text-[11px] tracking-[0.25em] uppercase">
              {label.calendar}
            </span>
          </div>

          <button
            type="button"
            onClick={handleCTA}
            data-testid="wm-banner-cta"
            className="mt-8 group inline-flex items-center gap-3 px-7 py-4 bg-[#D4AF37] text-black font-display text-base tracking-[0.22em] uppercase hover:bg-[#A93043] hover:text-white transition-colors"
          >
            {label.cta}
            <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
          </button>
        </div>

        {/* Right: Countdown */}
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-5 lg:justify-end">
            <span className="h-px w-6 bg-[#A93043]" />
            <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white/55">
              {c.isLive ? label.live : label.countdown}
            </span>
          </div>

          <div
            data-testid="wm-countdown"
            className="grid grid-cols-4 gap-2 sm:gap-3"
          >
            <CountdownCell value={c.days} label={label.days} testId="cd-days" />
            <CountdownCell value={c.hours} label={label.hours} testId="cd-hours" />
            <CountdownCell value={c.minutes} label={label.minutes} testId="cd-minutes" />
            <CountdownCell value={c.seconds} label={label.seconds} testId="cd-seconds" accent />
          </div>

          <p className="mt-4 font-serif-luxury italic text-sm text-[#D4AF37]/70 text-right">
            Vol. 02 — The Cup Edition
          </p>

          {/* Kickoff Bonus hook */}
          <button
            type="button"
            onClick={handleBonusCTA}
            data-testid="wm-bonus-cta"
            className="mt-6 w-full group flex items-center gap-4 px-5 py-4 border border-[#D4AF37]/40 bg-black/40 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-colors text-left"
          >
            <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-[#D4AF37] text-black font-display text-xl leading-none">
              15%
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-body text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-0.5">
                {label.bonus}
              </div>
              <div className="font-body text-xs sm:text-sm text-white/75 leading-snug">
                {label.bonusText}
              </div>
            </div>
            <span className="hidden sm:block font-display text-xs tracking-[0.25em] uppercase text-white group-hover:text-[#D4AF37] transition-colors whitespace-nowrap">
              {label.bonusCta}
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}

function CountdownCell({ value, label, testId, accent }) {
  const v = String(value).padStart(2, "0");
  return (
    <div
      data-testid={testId}
      className={`relative flex flex-col items-center justify-center py-5 sm:py-7 border ${
        accent ? "border-[#A93043]/50 bg-[#A93043]/10" : "border-[#1f1f29] bg-black/40"
      }`}
    >
      <span
        className={`font-display text-4xl sm:text-5xl lg:text-6xl leading-none ${
          accent ? "text-[#D4AF37]" : "text-white"
        }`}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {v}
      </span>
      <span className="mt-2 font-body text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-white/50">
        {label}
      </span>
    </div>
  );
}
